import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { newHttpService } from '../../services/new-http-service'
import { Backlog } from '../util-cmps/backlog'
import { useDraggable } from '../../hooks/use-draggable'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'

interface AlertEvent {
    id: number
    audit_control_id: number | null
    alert_type: string | null
    severity: string | null
    source: string | null
    service_name: string | null
    entity_name: string | null
    system_name: string | null
    status: string | null
    message: string | null
    metadata: Record<string, any> | null
    created_at: string | null
    last_seen: string | null
}

interface AlertEventLogLine {
    timestamp: number | null
    message: string
    log_group: string
    log_stream: string
    aws_region?: string | null
}

interface AlertEventLogsResponse {
    logs: AlertEventLogLine[]
    error_code?: string | null
    error_message?: string | null
}

export interface AlertEventFeedProps {
    data: AlertEvent[]
    hideToolbar?: boolean
    highlightPair?: string  // "Entity / System" key — auto-expands that pair on mount
    systemNameMap?: Record<string, string>  // lowercase system name → display name
    entityNameMap?: Record<string, string>  // lowercase entity name → display name
}

const ALERT_TYPE_LABELS: Record<string, string> = {
    etl_failure:    'ETL Failure',
    lambda_failure: 'Lambda Failure',
    ecs_failure:    'ECS Failure',
    old_agent_data: 'Old Agent Data',
    old_email_data: 'Old Email Data',
    old_sftp_data:  'Old SFTP Data',
}

const ALERT_TYPE_BUTTON_LABELS: Record<string, string> = {
    ...ALERT_TYPE_LABELS,
    old_agent_data: '🕵️ Old On-Prem Data',
    old_email_data: '📨 Old Email',
    old_sftp_data:  '📁 Old SFTP File',
}

const OLD_DATA_TYPES = ['old_sftp_data', 'old_agent_data', 'old_email_data']

const OLD_DATA_MESSAGE_PREFIXES: Record<string, string> = {
    old_sftp_data:  'ValueError: Old SFTP data',
    old_agent_data: 'ValueError: Old agent data',
    old_email_data: 'ValueError: Old email data',
}

const CUTOFF_MS: Record<string, number> = {
    '24h': 24 * 3600 * 1000,
    '7d':   7 * 24 * 3600 * 1000,
    'all':  Infinity,
}

type TimeRange = '24h' | '7d' | 'all'
type SortBy = 'status' | 'date' | 'entity' | 'system'
const MAX_INLINE_ALERTS = 3

function normalizeSystem(s: string): string {
    return s.replace(/^etl-/, '').replace(/-high-performance$/, '').replace(/2s3$/, '')
}

function resolveSystemName(systemName: string | null, serviceName: string | null): string | null {
    if (systemName) return systemName
    if (serviceName && serviceName !== 'generic') return serviceName
    return null
}

export function buildPairKey(entityName: string | null, systemName: string | null): string {
    return `${entityName || '—'} / ${systemName ? normalizeSystem(systemName) : '—'}`
}

function displayMessage(alert: AlertEvent): string {
    if (alert.alert_type === 'old_sftp_data' || alert.alert_type === 'old_agent_data' || alert.alert_type === 'old_email_data') {
        const meta = alert.metadata
        if (meta?.file_name || meta?.file_date) {
            const parts: string[] = []
            if (meta.file_name) parts.push(`File: ${meta.file_name}`)
            if (meta.file_date) parts.push(`last date: ${meta.file_date}`)
            return parts.join(' — ')
        }
    }
    return alert.message ?? '—'
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 2) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    const h = Math.floor(diff / 3600000)
    if (h < 24) return `${h}h ago`
    const d = Math.floor(h / 24)
    return d === 1 ? 'Yesterday' : `${d}d ago`
}

function formatAbsolute(iso: string): string {
    return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
}

function formatLogTimestamp(timestamp: number | null): string {
    if (!timestamp) return '—'
    return new Date(timestamp).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
}

function buildCloudWatchUrl(logGroup: string, logStream: string, awsRegion?: string | null): string {
    const region = awsRegion || 'eu-central-1'
    const encodedGroup = encodeURIComponent(encodeURIComponent(logGroup))
    const encodedStream = encodeURIComponent(encodeURIComponent(logStream))
    return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${encodedGroup}/log-events/${encodedStream}`
}

interface PairGroup {
    key: string
    alerts: AlertEvent[]           // all alerts for this pair, sorted newest first
    currentAlert: AlertEvent       // most recent alert overall (for header display)
    recentAlert: AlertEvent | null // most recent alert within 24h (resolve target)
    isResolved: boolean            // most recent alert is resolved
    isActive: boolean              // has a recent (24h) unresolved error
    isStale: boolean               // old unresolved error, outside 24h window
}

const ChevronIcon = ({ open }: { open: boolean }) => (
    <svg
        width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
)

interface AlertDetailsModalProps {
    alert: AlertEvent
    typeLabel: string | null
    onClose: () => void
}

const AlertDetailsModal: React.FC<AlertDetailsModalProps> = ({ alert, typeLabel, onClose }) => {
    useEscapeKey(onClose)
    useScrollLock()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()
    const [logs, setLogs] = useState<AlertEventLogLine[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const jobRunId = alert.metadata?.job_run_id ?? alert.metadata?.jobRunId ?? alert.metadata?.JobRunId ?? null
    const cwRegion = alert.metadata?.aws_region ?? alert.metadata?.awsRegion ?? 'eu-central-1'
    const cloudWatchUrl = jobRunId ? buildCloudWatchUrl('/aws-glue/python-jobs/error', jobRunId, cwRegion) : null
    const orderedLogs = useMemo(() => {
        return [...logs].sort((a, b) => {
            const aTs = a.timestamp ?? 0
            const bTs = b.timestamp ?? 0
            if (aTs !== bTs) return aTs - bTs
            return (a.log_group + a.log_stream + a.message).localeCompare(b.log_group + b.log_stream + b.message)
        })
    }, [logs])

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setHasError(false)
        setErrorMessage(null)
        newHttpService.get(`/api/backoffice/alert-events/${alert.id}/logs`, { limit: 10 })
            .then((res: AlertEventLogsResponse) => {
                if (!active) return
                setLogs(res?.logs || [])
                if (res?.error_message) setErrorMessage(res.error_message)
                else setErrorMessage(null)
                setHasError(!!res?.error_message)
            })
            .catch(() => {
                if (!active) return
                setHasError(true)
                setErrorMessage('Failed to load CloudWatch logs.')
                setLogs([])
            })
            .finally(() => {
                if (!active) return
                setIsLoading(false)
            })
        return () => { active = false }
    }, [alert.id])

    return <>
        <section ref={modalRef} className="form-modal aef-detail-modal" style={{ zIndex: 300 }} role="dialog" aria-modal="true" aria-label="Error details">
            <header onMouseDown={onHeaderMouseDown}>
                <h2>Error details</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <div className="aef-detail-body">
                <div className="aef-detail-grid">
                    <div><span>Type</span><strong>{typeLabel || alert.alert_type || '—'}</strong></div>
                    <div><span>Severity</span><strong>{alert.severity || '—'}</strong></div>
                    <div><span>Entity</span><strong>{alert.entity_name || '—'}</strong></div>
                    <div><span>System</span><strong>{resolveSystemName(alert.system_name, alert.service_name) || '—'}</strong></div>
                    <div><span>Service</span><strong>{alert.service_name || '—'}</strong></div>
                    <div><span>Job run ID</span><strong>{jobRunId || '—'}</strong></div>
                </div>

                <div className="aef-detail-section">
                    <span className="aef-detail-section-title">Message</span>
                    <pre className="aef-detail-message">{displayMessage(alert)}</pre>
                </div>

                <div className="aef-detail-section">
                    <div className="aef-detail-section-head">
                        <span className="aef-detail-section-title">CloudWatch logs</span>
                        {cloudWatchUrl && (
                            <a className="aef-detail-link" href={cloudWatchUrl} target="_blank" rel="noreferrer">
                                Open log stream
                            </a>
                        )}
                    </div>
                    {isLoading ? (
                        <div className="aef-detail-empty">Loading logs...</div>
                    ) : hasError ? (
                        <div className="aef-detail-error">{errorMessage || 'Failed to load CloudWatch logs.'}</div>
                    ) : orderedLogs.length === 0 ? (
                        <div className="aef-detail-empty">No matching CloudWatch logs found.</div>
                    ) : (
                        <div className="aef-cw-log-list">
                            {orderedLogs.map((log, idx) => (
                                <div className="aef-cw-log-line" key={`${log.log_group}-${log.log_stream}-${log.timestamp ?? idx}-${idx}`}>
                                    <div className="aef-cw-log-time">{formatLogTimestamp(log.timestamp)}</div>
                                    <pre className="aef-cw-log-message">{log.message}</pre>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
        <Backlog zIndex={299} onCloseBacklog={onClose} />
    </>
}

interface PairHistoryModalProps {
    group: PairGroup
    statusOverrides: Record<number, string>
    typeLabelForAlert: (alert: AlertEvent) => string | null
    onClose: () => void
}

const PairHistoryModal: React.FC<PairHistoryModalProps> = ({ group, statusOverrides, typeLabelForAlert, onClose }) => {
    useEscapeKey(onClose)
    useScrollLock()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    return <>
        <section ref={modalRef} className="form-modal aef-history-modal" style={{ zIndex: 300 }} role="dialog" aria-modal="true" aria-label="Alert history">
            <header onMouseDown={onHeaderMouseDown}>
                <h2>Alert history</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <div className="aef-history-body">
                <div className="aef-detail-grid">
                    <div><span>Pair</span><strong>{group.key}</strong></div>
                    <div><span>Total errors</span><strong>{group.alerts.length}</strong></div>
                    <div><span>Latest</span><strong>{formatAbsolute(group.currentAlert.created_at!)}</strong></div>
                </div>

                <div className="aef-history-list">
                    {group.alerts.map(alert => {
                        const effectiveStatus = alert.id in statusOverrides ? statusOverrides[alert.id] : alert.status
                        const isAlertResolved = effectiveStatus === 'resolved'
                        const typeLabel = typeLabelForAlert(alert)

                        return (
                            <div
                                key={alert.id}
                                className={`aef-log-entry${alert.id === group.currentAlert.id ? ' aef-log-entry--current' : ' aef-log-entry--history'}${isAlertResolved ? ' aef-log-entry--resolved' : ''}`}
                            >
                                <span className="aef-log-time" title={formatAbsolute(alert.created_at!)}>
                                    {formatAbsolute(alert.created_at!)}
                                </span>
                                <span className={`aef-log-severity aef-log-severity--${alert.severity?.toLowerCase()}`}>
                                    {alert.severity}
                                </span>
                                <span className="aef-log-type">{typeLabel}</span>
                                <span className="aef-log-message" title={displayMessage(alert)}>
                                    {displayMessage(alert)}
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
        <Backlog zIndex={299} onCloseBacklog={onClose} />
    </>
}

export const AlertEventFeed: React.FC<AlertEventFeedProps> = ({ data, hideToolbar = false, highlightPair, systemNameMap, entityNameMap }) => {
    const navigate = useNavigate()
    const [search, setSearch] = useState('')
    const [timeRange, setTimeRange] = useState<TimeRange>(highlightPair ? 'all' : '24h')
    const [alertTypeFilter, setAlertTypeFilter] = useState<Set<string>>(new Set())
    const [expanded, setExpanded] = useState<Set<string>>(() =>
        highlightPair ? new Set([highlightPair]) : new Set()
    )
    const [sortBy, setSortBy] = useState<SortBy>('status')
    const [statusOverrides, setStatusOverrides] = useState<Record<number, string>>({})
    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())
    const [selectedAlert, setSelectedAlert] = useState<AlertEvent | null>(null)
    const [selectedHistoryGroup, setSelectedHistoryGroup] = useState<PairGroup | null>(null)

    const groups = useMemo<PairGroup[]>(() => {
        const hasOldDataFilter = alertTypeFilter.size > 0 && OLD_DATA_TYPES.some(t => alertTypeFilter.has(t))
        const effectiveRange = hasOldDataFilter ? 'all' : timeRange
        const windowMs = CUTOFF_MS[effectiveRange]
        const cutoff24h = Date.now() - CUTOFF_MS['24h']
        const windowCutoff = isFinite(windowMs) ? Date.now() - windowMs : 0
        const map = new Map<string, AlertEvent[]>()

        data.forEach(a => {
            const key = buildPairKey(a.entity_name, resolveSystemName(a.system_name, a.service_name))
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(a)
        })

        const result: PairGroup[] = []
        map.forEach((alerts, key) => {
            alerts.sort((a, b) =>
                new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
            )
            const currentAlert = alerts[0]

            // pair must have at least one alert within the selected window
            if (new Date(currentAlert.created_at!).getTime() < windowCutoff) return

            // recentAlert is always 24h-based — drives red styling and resolve button
            const recentAlert = alerts.find(a => new Date(a.created_at!).getTime() >= cutoff24h) ?? null

            const resolveTarget = recentAlert ?? currentAlert
            const effectiveStatus = resolveTarget.id in statusOverrides
                ? statusOverrides[resolveTarget.id]
                : resolveTarget.status
            const isResolved = effectiveStatus === 'resolved'
            const isActive = !!recentAlert && !isResolved
            const isStale = !recentAlert && !isResolved

            result.push({ key, alerts, currentAlert, recentAlert, isResolved, isActive, isStale })
        })

        const rank = (g: { isActive: boolean; isStale: boolean }) =>
            g.isActive ? 0 : g.isStale ? 1 : 2

        result.sort((a, b) => {
            const r = rank(a) - rank(b)
            if (r !== 0) return r
            return new Date(b.currentAlert.created_at!).getTime() - new Date(a.currentAlert.created_at!).getTime()
        })

        return result
    }, [data, statusOverrides, timeRange, alertTypeFilter])

    const availableTypes = Object.keys(ALERT_TYPE_LABELS)

    const filtered = useMemo(() => {
        // in history views show everything; in 24h default, hide resolved unless toggled
        // when a type filter is active, show all pairs so old-data types (sftp/agent/email) are never hidden
        let result = (timeRange !== '24h' || alertTypeFilter.size > 0)
            ? groups
            : groups.filter(g => g.isActive)
        if (alertTypeFilter.size > 0) {
            result = result.filter(g =>
                g.alerts.some(a => {
                    if (a.alert_type && alertTypeFilter.has(a.alert_type)) return true
                    if (a.message) {
                        return Object.keys(OLD_DATA_MESSAGE_PREFIXES).some(type =>
                            alertTypeFilter.has(type) && a.message!.includes(OLD_DATA_MESSAGE_PREFIXES[type])
                        )
                    }
                    return false
                })
            )
        }
        if (search) {
            const term = search.toLowerCase()
            result = result.filter(g =>
                g.key.toLowerCase().includes(term) ||
                g.alerts.some(a => a.message?.toLowerCase().includes(term))
            )
        }
        if (sortBy === 'date') {
            result = [...result].sort((a, b) =>
                new Date(b.currentAlert.created_at!).getTime() - new Date(a.currentAlert.created_at!).getTime()
            )
        } else if (sortBy === 'entity') {
            result = [...result].sort((a, b) =>
                (a.key.split(' / ')[0] ?? '').localeCompare(b.key.split(' / ')[0] ?? '')
            )
        } else if (sortBy === 'system') {
            result = [...result].sort((a, b) =>
                (a.key.split(' / ')[1] ?? '').localeCompare(b.key.split(' / ')[1] ?? '')
            )
        }
        // 'status' keeps the existing rank sort from groups useMemo

        return result
    }, [groups, search, alertTypeFilter, timeRange, sortBy])

    const toggleExpand = useCallback((key: string) => {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }, [])

    const handleResolve = useCallback(async (alertId: number) => {
        setStatusOverrides(prev => ({ ...prev, [alertId]: 'resolved' }))
        setLoadingIds(prev => new Set(prev).add(alertId))
        try {
            await newHttpService.patch(`/api/backoffice/alert-events/${alertId}/status`, { status: 'resolved' })
        } catch {
            setStatusOverrides(prev => {
                const copy = { ...prev }
                delete copy[alertId]
                return copy
            })
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev)
                next.delete(alertId)
                return next
            })
        }
    }, [])

    const displayCount = timeRange === '24h'
        ? filtered.filter(g => g.isActive).length
        : filtered.filter(g => !g.isResolved).length
    const countLabel = timeRange === '24h' ? 'active' : 'unresolved'

    const lastHighlightedPairRef = useRef<string | undefined>(highlightPair)

    useEffect(() => {
        const previousHighlight = lastHighlightedPairRef.current

        setExpanded(prev => {
            const next = new Set(prev)
            if (previousHighlight && previousHighlight !== highlightPair) {
                next.delete(previousHighlight)
            }
            if (highlightPair) {
                next.add(highlightPair)
            }
            return next
        })

        lastHighlightedPairRef.current = highlightPair

        if (!highlightPair) return
        const el = document.getElementById(`aef-pair-${encodeURIComponent(highlightPair)}`)
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [highlightPair])

    const getTypeLabel = useCallback((alert: AlertEvent) => ALERT_TYPE_LABELS[alert.alert_type || ''] ?? alert.alert_type, [])

    return (
        <div className="aef-root">
            {!hideToolbar && (
                <div className="aef-toolbar">
                    <div className="aef-toolbar-row">
                        <span className="aef-count aef-count--error">{displayCount} {countLabel}</span>
                        <input
                            className="aef-search"
                            type="text"
                            placeholder="Search entity, system, message…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <div className="aef-filter-group">
                            {(['24h', '7d', 'all'] as TimeRange[]).map(r => (
                                <button
                                    key={r}
                                    className={`aef-filter-btn ${timeRange === r ? 'active' : ''}`}
                                    onClick={() => setTimeRange(r)}
                                >
                                    {r === '24h' ? 'Last 24h' : r === '7d' ? '7 days' : 'All time'}
                                </button>
                            ))}
                        </div>
                        <div className="aef-sort-control">
                            <span className="aef-sort-label">Sort by</span>
                            <select
                                className="aef-sort-select"
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value as SortBy)}
                            >
                                <option value="status">Status</option>
                                <option value="date">Newest first</option>
                                <option value="entity">Entity A–Z</option>
                                <option value="system">System A–Z</option>
                            </select>
                        </div>
                    </div>
                    <div className="aef-toolbar-row">
                        <div className="aef-filter-group">
                            {availableTypes.map(t => (
                                <button
                                    key={t}
                                    className={`aef-filter-btn ${alertTypeFilter.has(t) ? 'active' : ''}`}
                                    onClick={() => setAlertTypeFilter(prev => {
                                        const next = new Set(prev)
                                        if (next.has(t)) next.delete(t)
                                        else next.add(t)
                                        return next
                                    })}
                                >
                                    {ALERT_TYPE_BUTTON_LABELS[t] ?? t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="aef-pairs">
                {filtered.length === 0 ? (
                    <div className="aef-empty">
                        {search ? 'No pairs match your search.' : 'No active errors.'}
                    </div>
                ) : filtered.map(group => {
                    const isOpen = expanded.has(group.key)
                    const isHighlighted = group.key === highlightPair

                    return (
                        <div
                            key={group.key}
                            id={`aef-pair-${encodeURIComponent(group.key)}`}
                            className={`aef-pair ${group.isActive ? 'aef-pair--active' : group.isStale ? 'aef-pair--stale' : 'aef-pair--resolved'} ${isHighlighted ? 'aef-pair--highlighted' : ''}`}
                        >
                            <div className="aef-pair-header" onClick={() => toggleExpand(group.key)}>
                                <span className={`aef-pair-dot ${group.isActive ? 'aef-pair-dot--active' : group.isStale ? 'aef-pair-dot--stale' : 'aef-pair-dot--resolved'}`} />
                                <span className="aef-pair-name">{(() => {
                                    const [entity, sys] = group.key.split(' / ')
                                    const canon = (s: string) => s.toLowerCase().replace(/[\s\-_]+/g, '')
                                    const resolvedEntity = (entityNameMap && entity)
                                        ? (entityNameMap[entity.toLowerCase()] ?? entityNameMap[canon(entity)] ?? entity)
                                        : entity
                                    const resolvedSys = (systemNameMap && sys)
                                        ? (systemNameMap[sys.toLowerCase()] ?? systemNameMap[canon(sys)] ?? sys)
                                        : sys
                                    return `${resolvedEntity} / ${resolvedSys}`
                                })()}</span>
                                <span className="aef-pair-meta">
                                    {group.isResolved
                                        ? <span className="aef-pair-resolved-label">Resolved</span>
                                        : <span className="aef-pair-last-error">last error {relativeTime(group.currentAlert.created_at!)}</span>
                                    }
                                </span>
                                {group.currentAlert.audit_control_id && group.currentAlert.entity_name && (
                                    <span
                                        className="aef-pair-ac-link"
                                        title="Open audit control"
                                        onClick={e => {
                                            e.stopPropagation()
                                            navigate(`/companies/audit-controls?company=${encodeURIComponent(group.currentAlert.entity_name!.toLowerCase())}&expandedId=${group.currentAlert.audit_control_id}`)
                                        }}
                                    >
                                        ↗ AC
                                    </span>
                                )}
                                <span className="aef-pair-chevron">
                                    <ChevronIcon open={isOpen} />
                                </span>
                            </div>

                            {isOpen && (
                                <div className="aef-pair-log">
                                    {group.alerts.slice(0, MAX_INLINE_ALERTS).map(alert => {
                                        // highlight the resolve target if within 24h, otherwise the most recent overall
                                        const isCurrent = group.recentAlert
                                            ? alert.id === group.recentAlert.id
                                            : alert.id === group.currentAlert.id
                                        const effectiveStatus = alert.id in statusOverrides
                                            ? statusOverrides[alert.id]
                                            : alert.status
                                        const isAlertResolved = effectiveStatus === 'resolved'
                                        const typeLabel = getTypeLabel(alert)

                                        return (
                                            <div
                                                key={alert.id}
                                                className={`aef-log-entry${isCurrent ? ' aef-log-entry--current' : ' aef-log-entry--history'}${isAlertResolved ? ' aef-log-entry--resolved' : ''}`}
                                            >
                                                <span className="aef-log-time" title={formatAbsolute(alert.created_at!)}>
                                                    {formatAbsolute(alert.created_at!)}
                                                </span>
                                                <span className={`aef-log-severity aef-log-severity--${alert.severity?.toLowerCase()}`}>
                                                    {alert.severity}
                                                </span>
                                                <span className="aef-log-type">{typeLabel}</span>
                                                <span className="aef-log-message" title={displayMessage(alert)}>
                                                    {displayMessage(alert)}
                                                </span>
                                                <div className="aef-log-action">
                                                    <button
                                                        className="aef-details-btn"
                                                        onClick={e => { e.stopPropagation(); setSelectedAlert(alert) }}
                                                    >
                                                        View details
                                                    </button>
                                                    {isCurrent && !isAlertResolved && !!group.recentAlert ? (
                                                        <button
                                                            className="aef-resolve-btn"
                                                            disabled={loadingIds.has(alert.id)}
                                                            onClick={e => { e.stopPropagation(); handleResolve(alert.id) }}
                                                        >
                                                            {loadingIds.has(alert.id) ? '…' : 'Mark as resolved'}
                                                        </button>
                                                    ) : isAlertResolved ? (
                                                        <span className="aef-resolved-label">
                                                            ✓ Resolved
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {group.alerts.length > MAX_INLINE_ALERTS && (
                                        <div className="aef-history-footer">
                                            <button className="aef-details-btn" onClick={e => { e.stopPropagation(); setSelectedHistoryGroup(group) }}>
                                                View all {group.alerts.length} historical errors
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            {selectedAlert && (
                <AlertDetailsModal
                    alert={selectedAlert}
                    typeLabel={getTypeLabel(selectedAlert)}
                    onClose={() => setSelectedAlert(null)}
                />
            )}
            {selectedHistoryGroup && (
                <PairHistoryModal
                    group={selectedHistoryGroup}
                    statusOverrides={statusOverrides}
                    typeLabelForAlert={getTypeLabel}
                    onClose={() => setSelectedHistoryGroup(null)}
                />
            )}
        </div>
    )
}
